/*
All material copyright ESRI, All Rights Reserved, unless otherwise specified.
See https://js.arcgis.com/4.28/esri/copyright.txt for details.
*/
import { _ as e } from "../chunks/tslib.es6.js";
import { getAssetUrl as t } from "../assets.js";
import { when as s } from "../core/reactiveUtils.js";
import { property as a } from "../core/accessorSupport/decorators/property.js";
import "../core/accessorSupport/ensureType.js";
import "../core/arrayUtils.js";
import "../core/has.js";
import { subclass as i } from "../core/accessorSupport/decorators/subclass.js";
import { getBasemapThumbnailUrl as r, isBasemap3D as o, isBasemapInBeta as l } from "../support/basemapUtils.js";
import n from "./Widget.js";
import d from "./BasemapGallery/BasemapGalleryViewModel.js";
import { css as p } from "./BasemapGallery/css.js";
import { loadCalciteComponents as c } from "./support/componentsUtils.js";
import { globalCss as m } from "./support/globalCss.js";
import { Heading as h } from "./support/Heading.js";
import { accessibleHandler as u } from "./support/decorators/accessibleHandler.js";
import { messageBundle as g } from "./support/decorators/messageBundle.js";
import { tsx as v } from "./support/jsxFactory.js";
import { onResize as y } from "./support/widgetUtils.js";
const w = {
  small: 200,
  default: 280,
  wide: 420
};
let b = class extends n {
  constructor(e, t) {
    super(e, t), this.disabled = !1, this.headingLevel = 2, this.iconClass = p.widgetIcon, this.icon = null, this.messages = null, this.viewModel = new d(), this._focusBasemapItemEnabled = !1, this._container = null, this._width = 0, this._onContainerCreated = e => {
      this._container = e;
    };
  }
  initialize() {
    this.addHandles([y(() => this._container, ({
      contentRect: e
    }) => {
      this._width = e.width;
    }), s(() => this.source, () => this.viewModel.load(), {
      sync: !0,
      initial: !0,
      once: !0
    })]);
  }
  loadDependencies() {
    return c({
      scrim: () => import("@esri/calcite-components/dist/components/calcite-scrim.js"),
      chip: () => import("@esri/calcite-components/dist/components/calcite-chip.js")
    });
  }
  get activeBasemap() {
    return this.viewModel.activeBasemap;
  }
  set activeBasemap(e) {
    this.viewModel.activeBasemap = e;
  }
  get includeCurrentBasemap() {
    return this.viewModel.includeCurrentBasemap;
  }
  set includeCurrentBasemap(e) {
    this.viewModel.includeCurrentBasemap = e;
  }
  get label() {
    return this.messages?.widgetLabel ?? "";
  }
  set label(e) {
    this._overrideIfSome("label", e);
  }
  get source() {
    return this.viewModel.source;
  }
  set source(e) {
    this.viewModel.source = e;
  }
  get view() {
    return this.viewModel.view;
  }
  set view(e) {
    this.viewModel.view = e;
  }
  render() {
    const e = "loading" === this.source.state,
      t = this.disabled || "disabled" === this.viewModel.state,
      s = this.viewModel.items,
      a = {
        [p.sourceLoading]: e,
        [m.disabled]: t
      },
      i = this._width;
    i <= w.small || i >= w.wide ? a[p.layoutGrid] = !0 : i < w.default && (a[p.narrowItems] = !0);
    const r = e ? v("div", {
        class: p.loader,
        key: "loader"
      }) : null,
      o = e ? null : s.length > 0 ? v("ul", {
        "aria-disabled": this.disabled,
        "aria-label": this.label,
        bind: this,
        class: p.itemContainer,
        key: "item-container",
        onkeydown: this._handleKeyDown,
        role: "radiogroup"
      }, s.map((e, t) => this._renderBasemapGalleryItem(e, t)).toArray()) : v("div", {
        class: m.empty,
        key: "empty-message"
      }, v(h, {
        level: this.headingLevel
      }, this.messages.noBasemaps));
    return v("div", {
      afterCreate: this._onContainerCreated,
      class: this.classes(p.base, m.widget, m.panelHeightOnly, a),
      key: "container"
    }, 0 === this._width ? null : [r, o]);
  }
  _getRoundRobinIndex(e, t) {
    return (e + t) % t;
  }
  _handleKeyDown(e) {
    const {
      key: t
    } = e;
    if (!["ArrowUp", "ArrowDown", "ArrowRight", "ArrowLeft"].includes(t)) return;
    e.preventDefault();
    const {
        items: s,
        activeBasemapIndex: a
      } = this.viewModel,
      i = "ArrowUp" === t || "ArrowLeft" === t ? this._getRoundRobinIndex(Math.max(a - 1, -1), s.length) : this._getRoundRobinIndex(a + 1, s.length),
      r = s.at(i);
    "ready" === r?.state && (this.viewModel.activeBasemap = r.basemap), this._focusBasemapItemEnabled = !0;
  }
  _focusBasemapItem(e) {
    this._focusBasemapItemEnabled && 0 === e.tabIndex && (e.focus(), this._focusBasemapItemEnabled = !1);
  }
  _handleClick(e) {
    const t = e.currentTarget["data-item"];
    "ready" === t.state && (this.viewModel.activeBasemap = t.basemap);
  }
  _renderBasemapGalleryItem(e, s) {
    const a = r(e.basemap) || t("esri/themes/base/images/basemap-toggle-64.svg"),
      i = e.basemap.title,
      l = e.basemap.portalItem?.snippet,
      n = e.error?.message || l || i,
      {
        viewModel: {
          state: d,
          activeBasemapIndex: c
        }
      } = this,
      h = this.disabled || "disabled" === d,
      u = c === s,
      g = u || -1 === c && 0 === s ? 0 : -1,
      y = "loading" === d,
      w = {
        [p.selectedItem]: u,
        [p.itemError]: "error" === e.state
      },
      b = `basemapgallery-item-${e.uid}`;
    return v("li", {
      afterUpdate: this._focusBasemapItem,
      "aria-checked": u.toString(),
      "aria-disabled": h.toString(),
      "aria-labelledby": b,
      bind: this,
      class: this.classes(p.item, w),
      "data-item": e,
      key: e.uid,
      onclick: this._handleClick,
      onkeydown: this._handleClick,
      role: "radio",
      tabIndex: g,
      title: n
    }, v("img", {
      alt: "",
      class: p.itemThumbnail,
      src: a
    }), v("div", {
      class: p.itemContent,
      key: "content"
    }, v("div", {
      class: p.itemTitle,
      key: "title"
    }, v("span", {
      id: b
    }, i)), o(e.basemap) ? this._renderTags(e.basemap) : null), "loading" === e.state || u && y ? v("calcite-scrim", null, v("span", {
      "aria-hidden": "true",
      class: m.loaderAnimation,
      key: "loader",
      role: "presentation"
    })) : null);
  }
  _renderTags(e) {
    return v("div", {
      class: p.itemTagsContainer,
      key: "tag"
    }, this._render3DTag(), l(e) ? this._renderBetaTag() : null);
  }
  _render3DTag() {
    const {
      messages: e
    } = this;
    return v("calcite-chip", {
      key: "tag-3d",
      scale: "s",
      value: e.tag3D
    }, this.messages.tag3D);
  }
  _renderBetaTag() {
    const {
      messages: e
    } = this;
    return v("calcite-chip", {
      appearance: "outline-fill",
      key: "tag-beta",
      scale: "s",
      value: e.tagBeta
    }, this.messages.tagBeta);
  }
};
e([a()], b.prototype, "activeBasemap", null), e([a()], b.prototype, "disabled", void 0), e([a()], b.prototype, "headingLevel", void 0), e([a()], b.prototype, "iconClass", void 0), e([a()], b.prototype, "icon", void 0), e([a()], b.prototype, "label", null), e([a(), g("esri/widgets/BasemapGallery/t9n/BasemapGallery")], b.prototype, "messages", void 0), e([a()], b.prototype, "source", null), e([a()], b.prototype, "view", null), e([a()], b.prototype, "viewModel", void 0), e([a()], b.prototype, "_focusBasemapItemEnabled", void 0), e([a()], b.prototype, "_container", void 0), e([a()], b.prototype, "_width", void 0), e([u()], b.prototype, "_handleClick", null), b = e([i("esri.widgets.BasemapGallery")], b);
const _ = b;
export { _ as default };